import apiClient from './apiClient';

const apiBaseURL = '/ai-extraction/';

export type JobStatus = 'pending' | 'processing' | 'review_required' | 'completed' | 'failed' | 'cancelled';

export interface ExtractionJob {
  id: string;
  status: JobStatus;
  created_at: string;
  updated_at: string;
  error_message?: string;
  document_name: string;
  document_type?: string;
  document_size_kb?: string;
  requested_action: string;
  context: Record<string, any>;
  extraction_result?: any;
  routing_result?: any;
  preparation_result?: any;
  user_id: string;
  organization_id: string;
}

export interface ExtractionJobCreate {
  document_name: string;
  requested_action?: string;
  context?: Record<string, any>;
}

export interface ExtractionJobApprove {
  modified_preparation_result?: any;
}

export const aiExtractionService = {
  /**
   * Upload a document or text and start extraction
   */
  async uploadDocument(
    fileOrText: File | string,
    requestedAction: string = 'auto',
    context: Record<string, any> = {}
  ): Promise<ExtractionJob> {
    const formData = new FormData();
    
    if (typeof fileOrText === 'string') {
      // Create a text file from string with a meaningful name
      // Extract first few words for filename, or use timestamp
      const firstWords = fileOrText.substring(0, 50).replace(/[^a-zA-Z0-9\s]/g, '').trim();
      const filename = firstWords.length > 0 
        ? `${firstWords.substring(0, 30).replace(/\s+/g, '_')}_${Date.now()}.txt`
        : `text_extraction_${Date.now()}.txt`;
      
      const blob = new Blob([fileOrText], { type: 'text/plain' });
      const textFile = new File([blob], filename, { type: 'text/plain' });
      formData.append('file', textFile);
    } else {
      formData.append('file', fileOrText);
    }
    
    formData.append('requested_action', requestedAction);
    formData.append('context', JSON.stringify(context));

    const response = await apiClient.post(`${apiBaseURL}extract`, formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    });
    return response.data;
  },

  /**
   * List all extraction jobs
   */
  async listJobs(params: { skip?: number; limit?: number } = {}): Promise<ExtractionJob[]> {
    const response = await apiClient.get(`${apiBaseURL}jobs`, { params });
    return response.data;
  },

  /**
   * Get a specific job by ID
   */
  async getJob(jobId: string): Promise<ExtractionJob> {
    const response = await apiClient.get(`${apiBaseURL}jobs/${jobId}`);
    return response.data;
  },

  /**
   * Approve a job
   */
  async approveJob(jobId: string, approvalData?: ExtractionJobApprove): Promise<ExtractionJob> {
    const response = await apiClient.post(`${apiBaseURL}jobs/${jobId}/approve`, approvalData || {});
    console.log('approveJob response:', response.data);
    console.log('extraction_result in response:', response.data.extraction_result);
    return response.data;
  },

  /**
   * Reject a job
   */
  async rejectJob(jobId: string): Promise<ExtractionJob> {
    const response = await apiClient.post(`${apiBaseURL}jobs/${jobId}/reject`);
    return response.data;
  },

  /**
   * Delete all extraction jobs
   */
  async deleteAllJobs(): Promise<{ message: string; count: number }> {
    const response = await apiClient.delete(`${apiBaseURL}jobs`);
    return response.data;
  },
};

export default aiExtractionService;

